/**
 * Persistence helpers for per-report, per-user column selections.
 *
 * Column selections are stored in localStorage under keys prefixed with
 * `report_columns_`. They must survive login/logout so a user's chosen columns
 * are restored on the next session on the same browser. To keep different users
 * on the same browser isolated, keys are namespaced by the logged-in user's IEC.
 */

export const REPORT_COLUMN_PREFIX = 'report_columns_';

/**
 * Identifier for the current user, used to namespace their column selections.
 * Falls back to 'default' when no IEC is present (e.g. some admin logins).
 */
const getUserNamespace = () => {
  try {
    const iec = localStorage.getItem('iec_no');
    return iec && iec !== 'null' && iec !== 'undefined' ? iec : 'default';
  } catch (e) {
    return 'default';
  }
};

/**
 * Build the localStorage key for a report's column selection, scoped to the
 * current user. e.g. `report_columns_0388081678_BRCReport`.
 */
export const getReportColumnsStorageKey = (reportKey) => {
  if (!reportKey) return '';
  return `${REPORT_COLUMN_PREFIX}${getUserNamespace()}_${reportKey}`;
};

/**
 * Clear all localStorage EXCEPT saved report column selections. Use this in
 * place of `localStorage.clear()` on login/logout/session-expiry so that a
 * user's column preferences persist across sessions.
 */
export const clearStoragePreservingReportColumns = () => {
  try {
    const preserved = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(REPORT_COLUMN_PREFIX)) {
        preserved.push([key, localStorage.getItem(key)]);
      }
    }
    localStorage.clear();
    preserved.forEach(([key, value]) => {
      if (value !== null) localStorage.setItem(key, value);
    });
    // Dashboard filters / company picked in this browser-tab session belong to the
    // signed-in user — drop them so the next login starts from the defaults.
    sessionStorage.removeItem('cipDashFilters');
    sessionStorage.removeItem('selectedIEC');
  } catch (e) {
    // If anything goes wrong, fall back to the original behavior.
    try {
      localStorage.clear();
    } catch (_) {
      /* no-op */
    }
  }
};
